<template>
    <b-container fluid>
        <h1 class="text-left mb-4">{{ title }}</h1>
        <b-badge v-show="isLoading" variant="primary">{{
            $t('loading')
        }}</b-badge>
        <b-form
            v-if="!isLoading"
            ref="layers3DForm"
            class="pb-5"
            v-on:submit.prevent="$_onSubmit"
        >
            <div class="col-md-6">
                <!-- Name -->
                <x-input
                    :id="`name_${layer3D.id}`"
                    :text="$t('columnNameName')"
                    maxlength="50"
                    v-model="layer3D.name"
                    @input="$_onInputName"
                    :state="validName"
                    :invalidMessage="invalidNameErrorMessage"
                    required
                    autofocus
                >
                </x-input>
                <!-- URL -->
                <x-input
                    :id="`title_${layer3D.id}`"
                    :text="$t('columnUriTitle')"
                    maxlength="250"
                    v-model="layer3D.url"
                    @input="$_onInputUrl"
                    :state="validUri"
                    :invalidMessage="invalidUriErrorMessage"
                    required
                >
                </x-input>
            </div>
            <SubmitCancelRemove
                :is-saving="isSaving"
                :is-deleting="isDeleting"
                :mode="mode"
                :isBtnSaveDisabled="isBtnSaveDisabled"
                @cancel="$_onCancel"
                @remove="$_onRemove"
            >
            </SubmitCancelRemove>
        </b-form>
    </b-container>
</template>

<script>
    import { isValidUri } from '../../services/utilities';
    import { MODE_ADD, MODE_EDIT, TYPE_3D_LAYER } from '../../constants';
    import modalMixin from '../../components/Mixins/ModalMixin.vue';
    import XInput from '../../components/BaseComponents/x-input.vue';
    import SubmitCancelRemove from '../../components/SubmitCancelRemove.vue';

    export default {
        mixins: [modalMixin],
        components: {
            'x-input': XInput,
            SubmitCancelRemove
        },
        props: { id: { type: Number, required: false, default: -1 } },
        data() {
            return {
                mode: 0,
                title: '',
                isSaving: false,
                isDeleting: false,
                isLoading: false,
                invalidNameErrorMessage: '',
                invalidUriErrorMessage: '',
                layers3D: [],
                layer3D: {
                    name: '',
                    uri: '',
                    type3DLayer: TYPE_3D_LAYER.TILESET_3D
                }
            };
        },
        computed: {
            isEditMode() {
                return this.mode === MODE_EDIT;
            },
            isBtnSaveDisabled() {
                return (
                    !this.validName ||
                    !this.validUri ||
                    this.isSaving ||
                    this.isDeleting
                );
            },
            validName() {
                return this.invalidNameErrorMessage.length === 0;
            },
            validUri() {
                return this.invalidUriErrorMessage.length === 0;
            }
        },
        async created() {
            this.isLoading = true;
            await this.$_getAllLayers3D();
            if (this.id > 0) {
                this.mode = MODE_EDIT;
                this.title = this.$t('layers3D.edit');
                await this.$_getlayer3D();
            } else {
                this.mode = MODE_ADD;
                this.title = this.$t('layers3D.add');
            }
            this.isLoading = false;
        },
        methods: {
            $_onInputName() {
                // check if name already exists
                this.invalidNameErrorMessage = '';
                const found = this.layers3D.find((item) => {
                    return (
                        item.id !== this.id &&
                        item.name.toLowerCase() === this.layer3D.name.toLowerCase()
                    );
                });
                if (found) {
                    this.invalidNameErrorMessage = this.$t('layers3D.nameInUse');
                }
            },
            $_onInputUrl() {
                // check if uri is valid
                this.invalidUriErrorMessage = '';
                if (!isValidUri(this.layer3D.url)) {
                    this.invalidUriErrorMessage = this.$t('invalidUriTitle');
                }
            },
            async $_getlayer3D() {
                try {
                    const layer3D = await this.$store.dispatch('useApiData', {
                        name: 'GetLayer3D',
                        parameters: { Id: this.id }
                    });
                    this.layer3D = { ...layer3D?.data };
                } catch (error) {
                    const data = {
                        message: error.message,
                        variant: 'danger',
                        title: this.$t('layers3D.errorGet'),
                        autoHideDelay: 5000
                    };
                    this.isLoading = false;
                    this.showToast(data);
                }
            },
            async $_getAllLayers3D() {
                const layers3D = await this.$store.dispatch('useApiData', {
                    name: 'GetLayers3D',
                    parameters: {},
                    useLoader: true
                });
                this.layers3D = layers3D.data;
            },
            $_gotoOverview() {
                this.$router.push('/dashboard/layers3D');
            },
            $_onCancel() {
                this.$_gotoOverview();
            },
            async $_onRemove() {
                const confirm = await this.msgBoxConfirm({
                    message: this.$t('layers3D.deleteConfirmation')
                });
                if (confirm) {
                    try {
                        await this.$store.dispatch('useApiData', {
                            name: 'DeleteLayer3D',
                            parameters: { Id: this.id }
                        });
                        const data = {
                            message: this.$t('layers3D.removed'),
                            variant: 'success',
                            autoHideDelay: 5000
                        };
                        this.showToast(data);
                        this.$_gotoOverview();
                    } catch (error) {
                        const data = {
                            message: this.$t('deleteFailureMessage', [
                                `${error.message}`
                            ]),
                            variant: 'danger',
                            title: this.$t('deleteFailureTitle'),
                            autoHideDelay: 5000
                        };
                        this.showToast(data);
                        this.isDeleting = false;
                    } finally {
                        this.isDeleting = false;
                    }
                }
            },
            $_onSubmit() {
                if (this.validName && this.validUri) {
                    this.$_onSave();
                }
            },
            async $_onSave() {
                this.isSaving = true;
                const action = this.isEditMode ? 'Update' : 'Create';
                const name = `${action}Layer3D`;
                const body = {
                    ...this.layer3D
                };
                try {
                    await this.$store.dispatch('useApiData', {
                        name,
                        useLoader: true,
                        data: body
                    });

                    this.isSaving = false;
                    const data = {
                        message: this.$t('layers3D.saved'),
                        variant: 'success',
                        autoHideDelay: 5000
                    };
                    this.showToast(data);
                    if (this.mode === MODE_ADD) {
                        this.$_gotoOverview();
                    }
                } catch (error) {
                    const messageData = {
                        message: error.message,
                        variant: 'danger',
                        title: this.$t('saveFailureTitle'),
                        autoHideDelay: 5000
                    };
                    this.showToast(messageData);
                    this.isSaving = false;
                } finally {
                    this.isSaving = false;
                }
            }
        }
    };
</script>

<style lang="scss">
.hide-tabs-table-result {
    & .nav.nav-tabs {
        display: none;
    }
}
</style>
